<script setup>
/**
 * BulkPriorityModal — Fase 4.2 frontend
 *
 * Set priority on selected cards in one go. Reuses BulkStageFilter
 * (Fase 4.3) so the operator can narrow the operation to a single stage.
 *
 * Backend: POST /api/v1/accounts/:id/pipeline/bulk_actions/set_priority
 * (PipelineCardsAPI.bulkSetPriority).
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';
import BulkStageFilter from './BulkStageFilter.vue';

const props = defineProps({
  items: { type: Array, required: true },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const store = useStore();

const PRIORITY_OPTIONS = ['none', 'low', 'medium', 'high', 'urgent'];

const selectedPriority = ref('medium');
const stageFilter = ref('all');
const selectedIds = ref([]);

const stages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages)
    .map(([id, stage]) => ({ id, name: stage.name, position: stage.position }))
    .sort((a, b) => a.position - b.position);
});

const filteredItems = computed(() => {
  if (stageFilter.value === 'all') return props.items;
  return props.items.filter(
    item => (item.pipeline_stage || item.stage) === stageFilter.value
  );
});

const isAllSelected = computed(() => {
  const visibleIds = filteredItems.value.map(item => item.id);
  return (
    visibleIds.length > 0 &&
    visibleIds.every(id => selectedIds.value.includes(id))
  );
});

const toggleSelectAll = () => {
  const visibleIds = filteredItems.value.map(item => item.id);
  const allVisibleSelected = visibleIds.every(id =>
    selectedIds.value.includes(id)
  );
  if (allVisibleSelected) {
    selectedIds.value = selectedIds.value.filter(
      id => !visibleIds.includes(id)
    );
  } else {
    selectedIds.value = Array.from(
      new Set([...selectedIds.value, ...visibleIds])
    );
  }
};

const toggleItem = itemId => {
  const index = selectedIds.value.indexOf(itemId);
  if (index === -1) {
    selectedIds.value.push(itemId);
  } else {
    selectedIds.value.splice(index, 1);
  }
};

const priorityClass = priority => {
  const map = {
    urgent: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-9',
    high: 'bg-orange-3 text-orange-11 border-orange-9',
    medium: 'bg-n-amber-3 text-n-amber-11 border-n-amber-9',
    low: 'bg-blue-3 text-blue-11 border-blue-9',
    none: 'bg-n-alpha-2 text-n-slate-10 border-n-weak',
  };
  return map[priority] || map.none;
};

const handleConfirm = () => {
  emit('confirm', {
    cardIds:
      selectedIds.value.length > 0
        ? selectedIds.value
        : filteredItems.value.map(i => i.id),
    pipelineStage: stageFilter.value === 'all' ? null : stageFilter.value,
    priority: selectedPriority.value,
  });
};
</script>

<template>
  <div class="w-full max-w-[560px]">
    <div class="p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-1">
        {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.DESCRIPTION') }}
      </p>

      <!-- Stage filter (Fase 4.3) -->
      <div v-if="stages.length > 1" class="mb-4">
        <BulkStageFilter
          v-model="stageFilter"
          :items="items"
          :stages="stages"
        />
      </div>

      <!-- Item list with individual checkboxes -->
      <div
        class="max-h-48 overflow-y-auto mb-4 border border-n-weak rounded-xl"
      >
        <div
          class="flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-n-weak hover:bg-n-alpha-1 transition-colors rounded-t-xl"
          @click="toggleSelectAll"
        >
          <input
            type="checkbox"
            :checked="isAllSelected"
            class="size-4 flex-shrink-0 accent-n-brand"
            @click.stop
          />
          <span class="text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.BULK_ACTIONS.SELECT_ALL') }}
          </span>
          <span class="ml-auto text-label-small text-n-slate-10">
            {{
              selectedIds.filter(id => filteredItems.some(i => i.id === id))
                .length
            }}/{{ filteredItems.length }}
          </span>
        </div>
        <div
          v-for="(item, index) in filteredItems"
          :key="item.id"
          class="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
          :class="[
            selectedIds.includes(item.id)
              ? 'bg-n-alpha-1'
              : 'hover:bg-n-alpha-1',
            index < filteredItems.length - 1 ? 'border-b border-n-weak' : '',
            index === filteredItems.length - 1 ? 'rounded-b-xl' : '',
          ]"
          @click="toggleItem(item.id)"
        >
          <input
            type="checkbox"
            :checked="selectedIds.includes(item.id)"
            class="size-4 flex-shrink-0 accent-n-brand"
            @click.stop
          />
          <span class="text-label text-n-slate-12 truncate">
            {{
              item.item_details?.title ||
              item.title ||
              t('PIPELINE.ITEM_DETAILS.UNTITLED_ITEM')
            }}
          </span>
        </div>
      </div>

      <!-- Priority picker -->
      <div class="mb-6">
        <label class="text-label-small font-medium text-n-slate-11 mb-2 block">
          {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.PRIORITY_LABEL') }}
        </label>
        <div class="grid grid-cols-5 gap-2">
          <button
            v-for="opt in PRIORITY_OPTIONS"
            :key="opt"
            type="button"
            class="px-3 py-2 rounded-lg border text-label font-medium transition-all"
            :class="[
              selectedPriority === opt
                ? priorityClass(opt) + ' ring-2 ring-offset-1 ring-n-brand'
                : 'bg-n-alpha-1 text-n-slate-12 border-n-weak hover:bg-n-alpha-2',
            ]"
            @click="selectedPriority = opt"
          >
            {{ t(`PIPELINE.PRIORITY_LABELS.${opt.toUpperCase()}`) }}
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-between items-center gap-2">
        <span class="text-label-small text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.AFFECT_COUNT', {
              count: filteredItems.length,
            })
          }}
        </span>
        <div class="flex gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="$emit('close')"
          >
            {{ t('PIPELINE.BULK_ACTIONS.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :disabled="!filteredItems.length"
            @click="handleConfirm"
          >
            {{
              t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.CONFIRM', {
                count: filteredItems.length,
              })
            }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
